import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { AuthProvider, useAuth } from 'react-oidc-context';

import type { Staff } from '@/types/api';
import http, { setAccessToken, setActorId, setOnUnauthorized } from '@/utils/http';

import { oidcConfig, oidcEnabled } from './oidc-config';

/**
 * Identity, copied from spark-board. Two modes, chosen by runtime config:
 *  - OIDC (window.config.oidcIssuer set): Zitadel SSO. oidc-client-ts owns the tokens; we pass
 *    the ID token to the http layer as a Bearer and ask the backend who we are. Only people on
 *    the Staff list get in.
 *  - Legacy (no issuer): email sign-in without a password, sent as X-Actor-Id. Local work only.
 * Both expose the same context, so the rest of the app does not care which one runs.
 */
interface IdentityCtx {
  me: Staff | null;
  /** True under Zitadel SSO, false in the legacy email flow. */
  oidc: boolean;
  /** The session is resolving: show a spinner, not the sign-in screen. */
  loading: boolean;
  /** OIDC: Zitadel signed you in, but you are not on the Staff list or are deactivated. */
  denied: boolean;
  /** OIDC: why signing in stopped. session: Zitadel signed you in, but Prisma did not answer
   *  (retry asks it again); signin: the sign-in itself did not finish. */
  problem: 'session' | 'signin' | null;
  retry: () => void;
  /** Legacy email sign-in (throws in OIDC mode; the SSO screen never calls it). */
  signIn: (email: string, name?: string) => Promise<Staff>;
  /** Start the Zitadel redirect (OIDC mode). */
  loginWithSSO: () => void;
  signOut: () => void;
}

/** POST /session: legacy mode signs in with the email; OIDC mode reads the Bearer token and ignores the body. */
const postSession = (body: { email?: string; name?: string }) => http.post<Staff>('/session', body).then((r) => r.data);

const ME_KEY = 'prisma.me';
/** Where to return after an SSO round trip. Kept in sessionStorage, which survives the redirect
 *  to Zitadel and back in the same tab; read by the /auth/callback route. */
export const RETURN_TO_KEY = 'prisma.postLoginRedirect';
const captureReturnTo = () => {
  try {
    const p = window.location.pathname + window.location.search + window.location.hash;
    if (p && p !== '/' && !p.startsWith('/auth/callback')) sessionStorage.setItem(RETURN_TO_KEY, p);
  } catch {
    /* ignore unavailable storage */
  }
};

const Ctx = createContext<IdentityCtx | null>(null);

export const useIdentity = (): IdentityCtx => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useIdentity must be used within IdentityProvider');
  return v;
};

const loadMe = (): Staff | null => {
  try {
    const s = localStorage.getItem(ME_KEY);
    return s ? (JSON.parse(s) as Staff) : null;
  } catch {
    return null;
  }
};
const persistMe = (m: Staff | null) => {
  try {
    if (m) localStorage.setItem(ME_KEY, JSON.stringify(m));
    else localStorage.removeItem(ME_KEY);
  } catch {
    /* ignore storage failures (private mode) */
  }
};

/** Top-level provider: the OIDC bridge (under AuthProvider) or the legacy provider. */
export const IdentityProvider = ({ children }: { children: ReactNode }) => {
  if (oidcEnabled) {
    return (
      <AuthProvider {...oidcConfig}>
        <OidcIdentityProvider>{children}</OidcIdentityProvider>
      </AuthProvider>
    );
  }
  return <LegacyIdentityProvider>{children}</LegacyIdentityProvider>;
};

/** OIDC bridge: runs inside AuthProvider so it can use useAuth(). */
function OidcIdentityProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [me, setMe] = useState<Staff | null>(null);
  const [resolving, setResolving] = useState(false);
  const [denied, setDenied] = useState(false);
  const [sessionFailed, setSessionFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  // On a 401 the token is expired or invalid: drop it and start the login redirect again.
  useEffect(() => {
    setOnUnauthorized(() => {
      setAccessToken(null);
      captureReturnTo();
      void auth.signinRedirect();
    });
    return () => setOnUnauthorized(null);
  }, [auth]);

  // Keep the Bearer in step with the current ID token (sign-in and silent renewal).
  useEffect(() => {
    setAccessToken(auth.user?.id_token ?? null);
  }, [auth.user?.id_token]);

  // Once signed in, ask the backend who we are (it creates the staff row the first time).
  useEffect(() => {
    let cancelled = false;
    if (auth.isAuthenticated && auth.user) {
      setAccessToken(auth.user.id_token ?? null); // set before the request goes out
      setResolving(true);
      setSessionFailed(false);
      postSession({})
        .then((m) => {
          if (cancelled) return;
          setMe(m);
          persistMe(m);
        })
        .catch((err: unknown) => {
          // A 401 (bad token) starts the sign-in again; a 403 means Prisma does not admit you;
          // anything else (a deploy, a 500) is said, with a way to ask again.
          const status = (err as { response?: { status?: number } }).response?.status;
          if (cancelled || status === 401) return;
          if (status === 403) setDenied(true);
          else setSessionFailed(true);
        })
        .finally(() => {
          if (!cancelled) setResolving(false);
        });
    } else {
      setMe(null);
      persistMe(null);
      setDenied(false);
    }
    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, auth.user, attempt]);

  const signIn = useCallback(async (): Promise<Staff> => {
    throw new Error('email sign-in is disabled when SSO is enabled');
  }, []);
  const loginWithSSO = useCallback(() => {
    captureReturnTo();
    void auth.signinRedirect();
  }, [auth]);
  const signOut = useCallback(() => {
    setMe(null);
    persistMe(null);
    setAccessToken(null);
    void auth.signoutRedirect();
  }, [auth]);

  const loading = auth.isLoading || resolving || auth.activeNavigator != null;

  const problem = sessionFailed ? 'session' : auth.error ? 'signin' : null;
  return <Ctx.Provider value={{ me, oidc: true, loading, denied, problem, retry, signIn, loginWithSSO, signOut }}>{children}</Ctx.Provider>;
}

const noop = () => {};

/** Legacy email identity, no password. */
function LegacyIdentityProvider({ children }: { children: ReactNode }) {
  // `me` comes from storage; http.ts already seeded X-Actor-Id from its own key.
  const [me, setMe] = useState<Staff | null>(loadMe);

  // Who the stored id is now, read without writing anything: a name or email changed on
  // the Staff page shows here, and is never written back. Only when the row is gone (a
  // reset of the local database) is the stored email signed in again, so what this person
  // does is recorded under them.
  useEffect(() => {
    const stored = loadMe();
    if (!stored) return;
    const keep = (m: Staff) => {
      setActorId(m.id);
      persistMe(m);
      setMe(m);
    };
    http
      .get<Staff>('/session')
      .then((r) => keep(r.data))
      .catch((err: unknown) => {
        if ((err as { response?: { status?: number } }).response?.status !== 401 || !stored.email) return; // the backend is down: keep it
        return postSession({ email: stored.email, name: stored.name }).then(keep);
      })
      .catch(() => {
        /* keep the stored identity */
      });
  }, []);

  const signIn = useCallback(async (email: string, name?: string) => {
    const m = await postSession({ email, name });
    setActorId(m.id);
    persistMe(m);
    setMe(m);
    return m;
  }, []);
  const loginWithSSO = useCallback(() => {
    /* no-op in legacy mode */
  }, []);
  const signOut = useCallback(() => {
    setActorId(null);
    persistMe(null);
    setMe(null);
  }, []);

  return <Ctx.Provider value={{ me, oidc: false, loading: false, denied: false, problem: null, retry: noop, signIn, loginWithSSO, signOut }}>{children}</Ctx.Provider>;
}
